import {
  getCoverPreviewOutputSize,
  PROJECT_CARD_ASPECT_RATIO,
  PROJECT_CARD_THUMB_2X_HEIGHT,
  PROJECT_CARD_THUMB_2X_WIDTH,
} from './projectCoverAspect';

/**
 * Centre-cover crop at a fixed aspect ratio (ticket-2 automatic crop).
 * Matches the largest area of the image that fits `aspectRatio` without letterboxing.
 *
 * @param {number} naturalWidth
 * @param {number} naturalHeight
 * @param {number} aspectRatio width / height
 * @returns {{ x: number, y: number, width: number, height: number } | null}
 */
export function getAutomaticCoverCrop(
  naturalWidth,
  naturalHeight,
  aspectRatio
) {
  if (
    !naturalWidth ||
    !naturalHeight ||
    !aspectRatio ||
    aspectRatio <= 0 ||
    !Number.isFinite(naturalWidth) ||
    !Number.isFinite(naturalHeight) ||
    !Number.isFinite(aspectRatio)
  ) {
    return null;
  }

  const imageAspect = naturalWidth / naturalHeight;
  let width;
  let height;
  let x;
  let y;

  if (imageAspect > aspectRatio) {
    height = naturalHeight;
    width = height * aspectRatio;
    x = (naturalWidth - width) / 2;
    y = 0;
  } else {
    width = naturalWidth;
    height = width / aspectRatio;
    x = 0;
    y = (naturalHeight - height) / 2;
  }

  return { x, y, width, height };
}

/**
 * Convert pixel crop box → normalised 0–1 fractions of the source image.
 *
 * @param {{ x: number, y: number, width: number, height: number }} crop
 * @param {number} naturalWidth
 * @param {number} naturalHeight
 * @returns {{ x: number, y: number, w: number, h: number } | null}
 */
export function toNormalisedCrop(crop, naturalWidth, naturalHeight) {
  if (
    !crop ||
    !naturalWidth ||
    !naturalHeight ||
    !Number.isFinite(crop.x) ||
    !Number.isFinite(crop.y) ||
    !Number.isFinite(crop.width) ||
    !Number.isFinite(crop.height)
  ) {
    return null;
  }

  return {
    x: clamp01(crop.x / naturalWidth),
    y: clamp01(crop.y / naturalHeight),
    w: clamp01(crop.width / naturalWidth),
    h: clamp01(crop.height / naturalHeight),
  };
}

/**
 * Convert normalised 0–1 crop → pixel box for cropper / API.
 *
 * @param {{ x: number, y: number, w: number, h: number }} normalised
 * @param {number} naturalWidth
 * @param {number} naturalHeight
 * @returns {{ x: number, y: number, width: number, height: number } | null}
 */
export function fromNormalisedCrop(normalised, naturalWidth, naturalHeight) {
  if (
    !normalised ||
    !naturalWidth ||
    !naturalHeight ||
    !Number.isFinite(normalised.x) ||
    !Number.isFinite(normalised.y) ||
    !Number.isFinite(normalised.w) ||
    !Number.isFinite(normalised.h)
  ) {
    return null;
  }

  return {
    x: normalised.x * naturalWidth,
    y: normalised.y * naturalHeight,
    width: normalised.w * naturalWidth,
    height: normalised.h * naturalHeight,
  };
}

/**
 * Load an HTMLImageElement from a File, Blob, or URL string.
 *
 * @param {File|Blob|string} source
 * @returns {Promise<HTMLImageElement>}
 */
export function loadCoverImage(source) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let objectUrl = null;

    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };

    img.onload = () => {
      cleanup();
      resolve(img);
    };
    img.onerror = () => {
      cleanup();
      reject(new Error('Could not load image for thumbnail'));
    };

    if (typeof source === 'string') {
      // Cross-origin URLs may taint canvas; still useful for dimensions / CSS crop.
      img.crossOrigin = 'anonymous';
      img.src = source;
      return;
    }

    objectUrl = URL.createObjectURL(source);
    img.src = objectUrl;
  });
}

/**
 * Draw a pixel crop to a JPEG data URL at the given output size.
 *
 * @param {CanvasImageSource} image
 * @param {{ x: number, y: number, width: number, height: number }} crop
 * @param {{ width?: number, height?: number }} [output]
 * @returns {string|null} data URL or null if canvas is tainted / unavailable
 */
export function renderCoverThumbnail(
  image,
  crop,
  output = {
    width: PROJECT_CARD_THUMB_2X_WIDTH,
    height: PROJECT_CARD_THUMB_2X_HEIGHT,
  }
) {
  if (
    typeof document === 'undefined' ||
    !image ||
    !crop ||
    !output.width ||
    !output.height
  ) {
    return null;
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = output.width;
    canvas.height = output.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      output.width,
      output.height
    );

    return canvas.toDataURL('image/jpeg', 0.85);
  } catch (err) {
    return null;
  }
}

/**
 * Generate an automatic centre crop + 2× rendered preview for an image file/URL.
 *
 * @param {File|Blob|string} source
 * @param {number} [aspectRatio=PROJECT_CARD_ASPECT_RATIO]
 * @returns {Promise<{
 *   normalised: { x: number, y: number, w: number, h: number },
 *   pixel: { x: number, y: number, width: number, height: number },
 *   previewUrl: string|null,
 *   naturalWidth: number,
 *   naturalHeight: number,
 * }|null>}
 */
export async function generateAutomaticCover(
  source,
  aspectRatio = PROJECT_CARD_ASPECT_RATIO
) {
  const image = await loadCoverImage(source);
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  const pixel = getAutomaticCoverCrop(naturalWidth, naturalHeight, aspectRatio);
  if (!pixel) {
    return null;
  }

  const normalised = toNormalisedCrop(pixel, naturalWidth, naturalHeight);
  if (!normalised) {
    return null;
  }

  const output = getCoverPreviewOutputSize(aspectRatio);

  return {
    normalised,
    pixel,
    previewUrl: renderCoverThumbnail(image, pixel, output),
    naturalWidth,
    naturalHeight,
  };
}

function clamp01(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (value < 0) {
    return 0;
  }
  if (value > 1) {
    return 1;
  }
  return value;
}
